import { Link } from 'react-router-dom';
import {
  FaCloud, FaCode, FaChartBar, FaShieldAlt, FaServer, FaRobot,
  FaArrowRight, FaCheckCircle,
} from 'react-icons/fa';

const services = [
  {
    icon: <FaCode size={28} className="text-blue-500" />,
    bg: 'bg-blue-50',
    title: 'IT Consulting & Strategy',
    desc: 'We partner with your leadership to define a clear technology roadmap aligned with your business goals. From IT assessments to architecture design, our consultants bring strategic insight that drives transformation.',
    features: ['Technology Assessments', 'IT Roadmap Planning', 'Architecture Review', 'Digital Strategy', 'Vendor Selection'],
  },
  {
    icon: <FaCloud size={28} className="text-cyan-500" />,
    bg: 'bg-cyan-50',
    title: 'Cloud Solutions',
    desc: 'Accelerate your cloud journey with our certified engineers on Azure, AWS, and GCP. We handle everything from initial migration to ongoing cloud operations and cost optimization.',
    features: ['Cloud Migration', 'Infrastructure as Code', 'Cost Optimization', 'Multi-Cloud Architecture', 'Cloud Security'],
  },
  {
    icon: <FaCode size={28} className="text-violet-500" />,
    bg: 'bg-violet-50',
    title: 'Software Development',
    desc: 'Custom-built enterprise applications delivered with modern engineering practices. Our agile teams specialize in .NET, Java, React, Angular, and full-stack web development.',
    features: ['Custom App Development', '.NET & Python, Java Backends', 'React / Angular / Vue', 'API Integration', 'Legacy Modernization'],
  },
  {
    icon: <FaServer size={28} className="text-orange-500" />,
    bg: 'bg-orange-50',
    title: 'Managed IT Services',
    desc: 'Offload your IT operations to us. Our managed services team provides 24/7 monitoring, helpdesk support, and proactive maintenance so your team can focus on the business.',
    features: ['24/7 Monitoring', 'Helpdesk Support', 'Patch Management', 'Disaster Recovery', 'SLA-backed Performance'],
  },
  {
    icon: <FaChartBar size={28} className="text-emerald-500" />,
    bg: 'bg-emerald-50',
    title: 'Data & Analytics',
    desc: 'Turn your data into a competitive advantage. Our data engineers and analysts build pipelines, warehouses, and dashboards that deliver actionable insights to every level of your organization.',
    features: ['Data Warehousing', 'Power BI Dashboards', 'ETL Pipelines', 'SQL & Oracle Optimization', 'Machine Learning'],
  },
  {
    icon: <FaShieldAlt size={28} className="text-red-500" />,
    bg: 'bg-red-50',
    title: 'Cybersecurity',
    desc: 'Protect your organization from evolving threats. From security audits to compliance frameworks and incident response, our security experts keep your systems and data safe.',
    features: ['Security Audits', 'Penetration Testing', 'Compliance (SOC 2, HIPAA)', 'Identity & Access Management', 'Incident Response'],
  },
];

const process = [
  { step: '01', title: 'Discovery', desc: 'We begin with a deep-dive assessment of your current environment, pain points, and strategic objectives.' },
  { step: '02', title: 'Strategy', desc: 'Our architects design a tailored solution roadmap with clear milestones, timelines, and success metrics.' },
  { step: '03', title: 'Execution', desc: 'Experienced consultants implement with agile discipline, continuous communication, and rigorous quality standards.' },
  { step: '04', title: 'Optimize', desc: 'Post-delivery, we monitor, tune, and continuously improve to ensure long-term value and performance.' },
];

const techPartners = [
  'Microsoft Azure', 'AWS', 'Google Cloud', 'Oracle', 'ServiceNow',
  'Salesforce', 'SAP', 'IBM', 'VMware', 'Cisco',
];

export default function Services() {
  return (
    <div className="bg-white">

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0c1b4d] to-[#1d4ed8] text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        <div className="relative max-w-7xl mx-auto px-4">
          <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">What We Offer</span>
          <h1 className="text-4xl md:text-5xl font-bold mt-2 mb-4">Our Services</h1>
          <p className="text-blue-200 max-w-2xl text-lg leading-relaxed">
            Comprehensive IT solutions designed to modernize your infrastructure, accelerate development, and secure your digital future.
          </p>
          <div className="mt-7">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
              Get a Free Consultation <FaArrowRight size={13} />
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 40" className="w-full fill-white" preserveAspectRatio="none">
            <path d="M0,40 C480,0 960,40 1440,0 L1440,40 Z" />
          </svg>
        </div>
      </section>

      {/* Service Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((s, i) => (
              <div key={i} className="border border-gray-100 rounded-2xl p-7 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className={`w-14 h-14 ${s.bg} rounded-2xl flex items-center justify-center mb-5`}>
                  {s.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0c1b4d] mb-3">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{s.desc}</p>
                <ul className="space-y-2">
                  {s.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                      <FaCheckCircle className="text-cyan-500 flex-shrink-0" size={13} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI/Automation highlight */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-gradient-to-r from-[#0c1b4d] to-[#1d4ed8] rounded-3xl p-10 md:p-14 text-white flex flex-col md:flex-row gap-8 items-center">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-white bg-opacity-10 rounded-2xl flex items-center justify-center">
                <FaRobot size={36} className="text-cyan-400" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold mb-3">AI & Intelligent Automation</h3>
              <p className="text-blue-200 leading-relaxed mb-4">
                We help organizations harness the power of AI, machine learning, and RPA to automate repetitive processes, reduce costs, and create smarter workflows. From chatbots and predictive analytics to enterprise AI strategy, our team brings cutting-edge expertise to every engagement.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Machine Learning', 'NLP / ChatBots', 'RPA / Process Automation', 'Predictive Analytics', 'AI Strategy'].map((t, i) => (
                  <span key={i} className="px-3 py-1 bg-white bg-opacity-10 rounded-full text-xs text-blue-200 border border-white border-opacity-20">
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold px-6 py-3 rounded-xl transition-colors whitespace-nowrap">
                Learn More <FaArrowRight size={13} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-cyan-600 text-sm font-semibold uppercase tracking-widest">How We Work</span>
            <h2 className="text-3xl font-bold text-[#0c1b4d] mt-2 mb-3">Our Delivery Process</h2>
            <p className="text-gray-500 max-w-xl mx-auto">A proven four-step approach that consistently delivers successful outcomes for our clients.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p, i) => (
              <div key={i} className="relative p-6 rounded-2xl bg-slate-50 border border-gray-100">
                <div className="text-5xl font-extrabold text-[#0c1b4d] opacity-10 mb-3">{p.step}</div>
                <h3 className="font-bold text-[#0c1b4d] text-lg mb-2">{p.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
                {i < process.length - 1 && (
                  <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 text-gray-300">
                    <FaArrowRight size={16} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Partners */}
      <section className="py-14 bg-slate-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-center text-lg font-bold text-[#0c1b4d] mb-2">Technology Partners & Platforms</h2>
          <p className="text-center text-gray-400 text-sm mb-8">We work with the world's leading technology vendors</p>
          <div className="flex flex-wrap justify-center gap-3">
            {techPartners.map((t, i) => (
              <span key={i} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-600 hover:border-blue-300 hover:text-blue-700 transition-colors shadow-sm cursor-default">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-[#0c1b4d] mb-4">Not sure which service is right for you?</h2>
          <p className="text-gray-500 mb-8">Talk to one of our solutions experts — we'll help you find the best fit for your needs and budget.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3 bg-[#0c1b4d] text-white font-bold rounded-xl hover:bg-blue-900 transition-colors shadow-lg">
            Schedule a Free Consultation <FaArrowRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  );
}
